import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, TrendingUp, Layers, MapPin, Plus, Building2, Gavel,
  FileSpreadsheet, FileDown, Trash2, Search,
} from "lucide-react";
import { api, euro, STATUS_LABELS, STATUS_COLORS } from "../lib/api";

const KPI = ({ icon: Icon, label, value, tint, testid }) => (
  <div className="min-w-0 bg-white border border-slate-200 rounded-xl p-4 sm:p-5 flex items-start gap-3 sm:gap-4 fade-up" data-testid={testid}>
    <div className={`w-10 h-10 sm:w-11 sm:h-11 shrink-0 rounded-lg flex items-center justify-center ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-xl sm:text-2xl font-display font-bold tabular tracking-tight text-slate-900 break-words leading-tight">{value}</div>
      <div className="text-xs text-slate-500 mt-1 leading-snug">{label}</div>
    </div>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, s] = await Promise.all([api.listOffers(), api.stats()]);
      setOffers(o);
      setStats(s);
    } catch (e) {
      toast.error("Errore nel caricamento delle offerte");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminare questa offerta?")) return;
    await api.deleteOffer(id);
    toast.success("Offerta eliminata");
    load();
  };

  const filtered = offers.filter((o) => {
    const matchQ = !q || o.title?.toLowerCase().includes(q.toLowerCase()) || o.client_name?.toLowerCase().includes(q.toLowerCase());
    const matchType = !typeFilter || o.client_type === typeFilter;
    return matchQ && matchType;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-900">Dashboard Offerte</h1>
          <p className="text-slate-500 mt-1 text-sm">Gestisci offerte per privati e gare d'appalto, computi metrici e allegati.</p>
        </div>
        <button onClick={() => navigate("/nuova-offerta")} data-testid="dashboard-new-offer-button" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg self-start sm:self-auto"><Plus className="w-4 h-4" /> Nuova offerta</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPI icon={FileText} label="Offerte in corso" value={stats?.offers_in_progress ?? "—"} tint="bg-orange-50 text-orange-600" testid="kpi-in-progress" />
        <KPI icon={TrendingUp} label="Importo complessivo CME" value={stats ? euro(stats.total_amount) : "—"} tint="bg-sky-50 text-sky-600" testid="kpi-total-amount" />
        <KPI icon={Gavel} label="Tasso aggiudicazione gare" value={stats ? `${stats.win_rate}%` : "—"} tint="bg-emerald-50 text-emerald-600" testid="kpi-win-rate" />
        <KPI icon={Layers} label="Prezzari regionali attivi" value={stats?.active_prezzari ?? "—"} tint="bg-violet-50 text-violet-600" testid="kpi-prezzari" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo o committente…"
            data-testid="dashboard-search-input"
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {[["", "Tutte"], ["privato", "Privati"], ["gara", "Gare"]].map(([val, lab]) => (
            <button
              key={val}
              onClick={() => setTypeFilter(val)}
              data-testid={`filter-type-${val || "all"}`}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                typeFilter === val ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {lab}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm py-16 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">Nessuna offerta. Crea la tua prima offerta per iniziare.</p>
          <button onClick={() => navigate("/nuova-offerta")} data-testid="empty-new-offer-button" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            <Plus className="w-4 h-4" /> Nuova Offerta
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((o) => (
            <div
              role="link"
              tabIndex={0}
              onClick={() => navigate(`/offerte/${o.id}`)}
              onKeyDown={(e) => { if (e.key === "Enter") navigate(`/offerte/${o.id}`); }}
              key={o.id}
              data-testid={`offer-card-${o.id}`}
              className="group cursor-pointer bg-white border border-slate-200 rounded-xl p-5 hover:border-orange-400 hover:shadow-md transition-all fade-up"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md border ${o.client_type === "gara" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                  {o.client_type === "gara" ? <Gavel className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                  {o.client_type === "gara" ? "Gara d'Appalto" : "Privato"}
                </span>
                <span className={`text-[11px] font-medium px-2 py-1 rounded-md border ${STATUS_COLORS[o.status] || STATUS_COLORS.bozza}`}>
                  {STATUS_LABELS[o.status] || o.status}
                </span>
              </div>
              <h3 className="font-display font-semibold text-lg text-slate-900 tracking-tight leading-snug group-hover:text-orange-600 transition-colors">{o.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{o.client_name || "—"}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                {o.region && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{o.region}</span>}
                <span>{o.cme_count} voci CME</span>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide">Totale offerta</div>
                  <div className="font-mono font-semibold text-slate-900">{euro(o.cme_total)}</div>
                </div>
                <div className="flex items-center gap-1">
                  <a href={api.exportExcelUrl(o.id)} onClick={(e) => e.stopPropagation()} data-testid={`offer-excel-${o.id}`} className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600" title="Esporta Excel"><FileSpreadsheet className="w-4 h-4" /></a>
                  <a href={api.exportPdfUrl(o.id)} onClick={(e) => e.stopPropagation()} data-testid={`offer-pdf-${o.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Esporta PDF"><FileDown className="w-4 h-4" /></a>
                  <button onClick={(e) => handleDelete(o.id, e)} data-testid={`offer-delete-${o.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
